/**
 * THE GRAPPLE CHAIN — shoot it out, hook somebody, and one of you gets reeled in.
 *
 * The owner's third: "a chain like scorpion from mortal kombat that shoots out and grabs a
 * circle and pulls them into you (if the circle is bigger though, they would eat you)".
 *
 * **THE LIGHTER END MOVES**, and that one sentence is the whole implementation of the warning in
 * the brackets. The chain does not know who fired it; it hauls along itself, and mass decides
 * which way. Grapple something smaller and it comes to you. Grapple something bigger and *you*
 * go to *it* — and what happens when you arrive is not a special case either, it is
 * `stepPlayerCollisions` applying the same EAT_MASS_RATIO it applies to every other contact in
 * the game. There is no branch in the simulation that says "the firer loses"; there is a haul,
 * and then there is the room.
 *
 * That is also why the reversal is worth having rather than merely honest: a grapple is the only
 * action in the game whose *failure* is a decision you made, so aiming it is a judgement about
 * mass rather than about geometry.
 *
 * Three ways out, all bounded: the haul window runs out (`haulTicks`), the two ends get further
 * apart than the chain is long (`breakDistance` — the hauled player being dragged is still being
 * steered, so somebody strong enough to pull away does), or one of them stops being a legal
 * target. A duellist is never hooked in the first place and a hooked player who is frozen into a
 * duel drops the chain: frozen means frozen, and a duel that could be dragged out from under its
 * duellists would be the third-player snipe the whole freeze exists to stop.
 */

import { PROJECTILE_MUZZLE_CLEARANCE, TICK_RATE_HZ } from '../constants/simulation.js';
import type { PowerUp } from './powerup.js';

export const POWER_UP_GRAPPLE = 'grapple';

/** Faster than a rocket: the head is thinner and it has less far to go. */
export const GRAPPLE_SPEED = 1_100;

/**
 * How far the head may travel, in world units. Three quarters of the rocket's, because a chain
 * that reached as far as a rocket would be strictly the better item — a rocket only breaks
 * somebody up, and this decides where they *are*.
 */
export const GRAPPLE_RANGE = 900;

/** Thin: it is a chain, and it is drawn as a line with a head on the end of it. */
export const GRAPPLE_RADIUS = 16;

export const GRAPPLE_LIFETIME_TICKS = Math.ceil((GRAPPLE_RANGE / GRAPPLE_SPEED) * TICK_RATE_HZ);

/**
 * How long the haul runs once the chain connects, in ticks — a second and a half.
 *
 * Long enough to close GRAPPLE_RANGE at `GRAPPLE_HAUL_SPEED` and short enough that being hooked
 * is a moment rather than a state: whatever the chain was going to decide, it has decided by the
 * time this runs out, and the pair are left to the ordinary rules.
 */
export const GRAPPLE_HAUL_TICKS = Math.round(1.5 * TICK_RATE_HZ);

/**
 * World units per second the hauled end is dragged at, on top of its own steering.
 *
 * A little under twice BASE_SPEED, so a hooked player is genuinely losing ground rather than
 * merely being slowed, and well below the rocket's, so the haul is a drag and not a throw.
 * `GRAPPLE_HAUL_TICKS` of it covers 960 units, which is more than GRAPPLE_RANGE — a connected
 * grapple closes the gap it opened across unless something breaks it, and a chain that could not
 * do that would be one you could safely fire at your maximum reach and then ignore.
 */
export const GRAPPLE_HAUL_SPEED = 640;

/**
 * Past this much distance between the two ends, the chain snaps, in world units.
 *
 * Wider than GRAPPLE_RANGE because the hauled player is still being steered while they are
 * dragged: the gap can grow before it shrinks, and a chain that broke on the first tick of
 * somebody pulling against it would never haul anybody who was running away — which is everybody
 * worth hooking. This is what "the target breaking free" is, and it is a distance rather than a
 * strength test so that it is the same for a bot and for a browser.
 */
export const GRAPPLE_BREAK_DISTANCE = 1_400;

/**
 * How long the window runs, in seconds. **Thirty**, and it is the player's own number: "the
 * grappling hook can be used for 30 seconds."
 *
 * Three times the rocket's window, and the difference is what each of them is worth per firing: a
 * rocket is a shatter that happens to whoever is in the way, and a chain is a *decision* — you
 * have to pick something the mass arithmetic says you can survive arriving next to, and getting
 * that wrong feeds you to them. Thirty seconds is long enough for a player to be picky, which is
 * exactly what makes the ability read as a hook rather than as a second rocket.
 */
export const GRAPPLE_DURATION_SECONDS = 30;
export const GRAPPLE_DURATION_TICKS = GRAPPLE_DURATION_SECONDS * TICK_RATE_HZ;

/**
 * The gap between two chains, in ticks — **GRAPPLE_HAUL_TICKS, 1.5 s, twenty chains a window.**
 *
 * Derived rather than chosen, and it is the only number that keeps this ability coherent: a haul
 * runs for GRAPPLE_HAUL_TICKS, so a shorter cooldown would let one player have two chains hauling
 * two different people at once out of one press each — two of `stepHaul`'s bounded windows
 * overlapping, which is not a thing the mechanic has an answer for. Firing again the tick your
 * last haul ends is the fastest this can honestly go.
 */
export const GRAPPLE_FIRE_COOLDOWN_TICKS = GRAPPLE_HAUL_TICKS;

export const grapplePowerUp: PowerUp = {
  id: POWER_UP_GRAPPLE,
  name: 'Grapple',
  description: 'Hooks a player and reels the lighter of you in — mind what you grab.',
  glyph: '⛓️',
  use: {
    model: 'timed',
    durationTicks: GRAPPLE_DURATION_TICKS,
    cooldownTicks: GRAPPLE_FIRE_COOLDOWN_TICKS,
  },
  projectile: {
    motion: 'launched',
    effect: 'tether',
    speed: GRAPPLE_SPEED,
    range: GRAPPLE_RANGE,
    lifetimeTicks: GRAPPLE_LIFETIME_TICKS,
    armingTicks: 0,
    radius: GRAPPLE_RADIUS,
    standoff: PROJECTILE_MUZZLE_CLEARANCE,
    hitsOwner: false,
    haulTicks: GRAPPLE_HAUL_TICKS,
    haulSpeed: GRAPPLE_HAUL_SPEED,
    breakDistance: GRAPPLE_BREAK_DISTANCE,
  },
};
